// ── Provider Personal Blocks ─────────────────────────────────────────────────
// Iter 33d — Provider-owned recurring blocks (testing, meetings, planning,
// other). These persist as RecurringBlock documents with block_kind="personal"
// so they appear across Today / Week / Calendar views and feed the wizard as
// hard blocks. Each row supports:
//   • kind → color treatment across views
//   • weekday + start/end time
//   • optional date range (start_date / end_date)
//   • optional weeks_of_month cadence (e.g. last week of every month)
import { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FloppyDisk, Plus, Trash, Pencil, Briefcase } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { nativeOptions } from "@/lib/nativeOptions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PROVIDER_BLOCK_KINDS, providerBlockColor } from "@/lib/providerBlockColors";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const WEEK_OPTIONS = [1, 2, 3, 4, 5];

export default function ProviderPersonalBlocksCard({ provider }) {
  const [blocks, setBlocks] = useState([]);
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    if (!provider?.id) return;
    setLoading(true);
    try {
      // Active schedule period is required for RecurringBlock creation.
      const [p, rb] = await Promise.all([
        api.listSchedulePeriods().catch(() => ({ data: [] })),
        api.listRecurringBlocks({ provider_id: provider.id }),
      ]);
      const activePeriod = (p.data || []).find((x) => x.is_active) || (p.data || [])[0] || null;
      setPeriod(activePeriod);
      setBlocks((rb.data || []).filter((b) => b.block_kind === "personal"));
    } catch (e) {
      toast.error(`Failed to load provider blocks: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id]);

  if (!provider) return null;

  return (
    <Card
      className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm"
      data-testid="provider-personal-blocks-card"
    >
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
            <Briefcase size={16} weight="duotone" className="text-[#4A6B8C]" />
            Provider Block Schedule
            <span className="text-[10px] font-normal text-[#6B7280]">({blocks.length})</span>
          </CardTitle>
          <p className="text-[11px] text-[#6B7280] mt-1">
            Testing, meetings, planning, and other recurring time you own.
            Appears on Today / Week / Calendar and acts as a hard block in the wizard.
          </p>
        </div>
        {!adding && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdding(true)}
            disabled={!period}
            data-testid="provider-block-add"
            className="rounded-lg h-8 text-xs border-[#d4d2cc] disabled:opacity-40"
          >
            <Plus size={12} className="mr-1" />
            Add block
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {!period && !loading && (
          <p className="text-[11px] text-[#D9A05B] italic">
            No active schedule period. Create one to add provider blocks.
          </p>
        )}
        {loading ? (
          <p className="text-xs text-[#6B7280]">Loading…</p>
        ) : blocks.length === 0 && !adding ? (
          <p className="text-[11px] text-[#6B7280] italic">
            No provider blocks yet. Add a recurring block for testing, meetings, planning, or other.
          </p>
        ) : blocks.length > 0 ? (
          <div className="border border-[#E5E3DB] rounded-lg overflow-hidden">
            <div className="grid grid-cols-[90px_80px_130px_1fr_110px_120px_60px] gap-2 px-3 py-2 bg-[#F3F1EC] text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
              <span>Kind</span>
              <span>Day</span>
              <span>Time</span>
              <span>Label</span>
              <span>Weeks</span>
              <span>Dates</span>
              <span></span>
            </div>
            <div className="divide-y divide-[#ECEAE4]">
              {blocks.map((b) => (
                <ProviderBlockRow
                  key={b.id}
                  b={b}
                  period={period}
                  providerId={provider.id}
                  onSaved={load}
                />
              ))}
            </div>
          </div>
        ) : null}
        {adding && period && (
          <ProviderBlockEditor
            period={period}
            providerId={provider.id}
            onCancel={() => setAdding(false)}
            onSaved={() => {
              setAdding(false);
              load();
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ProviderBlockRow({ b, period, providerId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const color = providerBlockColor(b.personal_kind);
  const remove = async () => {
    setDeleting(true);
    try {
      // FP3-124 §8 — Delete personal-block series is effective-date
      // forward; historical calendar shows the block up through
      // yesterday.
      const _n = new Date();
      const _eff = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, "0")}-${String(_n.getDate()).padStart(2, "0")}`;
      await api.deleteRecurringBlock(b.id, _eff);
      toast.success("Deleted");
      setConfirming(false);
      onSaved();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  };
  if (editing) {
    return (
      <div className="px-3 py-2 bg-[#FAFAF7]">
        <ProviderBlockEditor
          period={period}
          providerId={providerId}
          existing={b}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onSaved();
          }}
        />
      </div>
    );
  }
  const weeksLabel = (() => {
    if (b.week_cadence && b.week_cadence > 1) return `every ${b.week_cadence}wk`;
    if (Array.isArray(b.weeks_of_month) && b.weeks_of_month.length > 0)
      return b.weeks_of_month.map((w) => `W${w}`).join(", ");
    return "every";
  })();
  const datesLabel = b.start_date || b.end_date
    ? `${b.start_date || "…"} → ${b.end_date || "ongoing"}`
    : "ongoing";
  // FP3-multiday — Prefer the explicit `days_of_week` subset; fall
  // back to the legacy `day_of_week` (-1 = Every day, 0..4 = single).
  const dayLabel = (() => {
    if (Array.isArray(b.days_of_week) && b.days_of_week.length > 0) {
      return b.days_of_week
        .map((d) => parseInt(d, 10))
        .filter((d) => d >= 0 && d <= 4)
        .sort((a, c) => a - c)
        .map((d) => DAY_LABELS[d])
        .join(", ");
    }
    if (b.day_of_week === -1) return "Every day";
    if (b.day_of_week >= 0 && b.day_of_week <= 4) return DAY_LABELS[b.day_of_week];
    return "—";
  })();
  const isAllDay = b.start_time === "00:00" && b.end_time === "23:59";
  const timeLabel = isAllDay ? "All day" : `${b.start_time}–${b.end_time}`;
  return (
    <>
      <div
        data-testid={`provider-block-row-${b.id}`}
        className="grid grid-cols-[90px_80px_130px_1fr_110px_120px_60px] gap-2 px-3 py-2 items-center text-[11px]"
      >
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium w-fit ${color.chip}`}
        >
          {b.personal_kind || "other"}
        </span>
        <span className="text-[#1F2937]">{dayLabel}</span>
        <span className="text-[#4B5563] tabular-nums">{timeLabel}</span>
        <span className="text-[#1F2937] truncate">{b.personal_label || "—"}</span>
        <span className="text-[#4B5563] text-[10px]">{weeksLabel}</span>
        <span className="text-[#6B7280] text-[10px] tabular-nums">{datesLabel}</span>
        <div className="flex justify-end gap-1">
          <button
            onClick={() => setEditing(true)}
            data-testid={`provider-block-edit-${b.id}`}
            className="text-[#6B7280] hover:text-[#1E3F33] p-1"
          >
            <Pencil size={11} />
          </button>
          <button
            onClick={() => setConfirming(true)}
            data-testid={`provider-block-delete-${b.id}`}
            className="text-[#6B7280] hover:text-[#B85C5C] p-1"
          >
            <Trash size={11} />
          </button>
        </div>
      </div>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent data-testid="provider-block-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete provider block?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {b.personal_kind} block on {dayLabel} {b.start_time}–{b.end_time}
              {b.personal_label ? ` (${b.personal_label})` : ""}. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="provider-block-delete-cancel"
              disabled={deleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              disabled={deleting}
              data-testid="provider-block-delete-confirm"
              className="bg-[#B85C5C] hover:bg-[#9b4747] text-white"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ProviderBlockEditor({ period, providerId, existing, onCancel, onSaved }) {
  // FP3-multiday — Resolve the initial selected-days set from either
  // the new `days_of_week` array OR the legacy `day_of_week` int.
  // Empty/missing → defaults to Mon. Length-5 (and -1 sentinel) →
  // "Every day" master chip lights up.
  const initialDays = (() => {
    if (existing) {
      if (Array.isArray(existing.days_of_week) && existing.days_of_week.length > 0) {
        return existing.days_of_week
          .map((d) => parseInt(d, 10))
          .filter((d) => d >= 0 && d <= 4);
      }
      const dow = parseInt(existing.day_of_week, 10);
      if (dow === -1) return [0, 1, 2, 3, 4];
      if (dow >= 0 && dow <= 4) return [dow];
    }
    return [1];
  })();
  const initial = existing
    ? {
        ...existing,
        days_of_week: initialDays,
        weeks_of_month: existing.weeks_of_month || [],
        week_cadence: existing.week_cadence || null,
        week_cadence_anchor: existing.week_cadence_anchor || "",
      }
    : {
        schedule_period_id: period.id,
        provider_id: providerId,
        student_ids: [],
        block_kind: "personal",
        personal_kind: "testing",
        personal_label: "",
        day_of_week: 1,
        days_of_week: initialDays,
        start_time: "09:00",
        end_time: "10:00",
        weeks_of_month: [],
        start_date: "",
        end_date: "",
        week_cadence: null,
        week_cadence_anchor: "",
      };
  const [draft, setDraft] = useState(initial);
  // Iter 33d — Detect "All day" by start=00:00 + end=23:59. Toggling on
  // pins the times; toggling off restores the prior 09:00-10:00 default.
  const isAllDay = draft.start_time === "00:00" && draft.end_time === "23:59";
  const [saving, setSaving] = useState(false);
  // FP3-multiday — Selected weekdays. We always work in array form
  // internally and only collapse to the legacy single-day / -1 shape
  // at save time.
  const selectedDays = useMemo(() => {
    const arr = Array.isArray(draft.days_of_week) ? draft.days_of_week : [];
    return arr
      .map((d) => parseInt(d, 10))
      .filter((d) => d >= 0 && d <= 4)
      .sort((a, b) => a - b);
  }, [draft.days_of_week]);
  const allDaysSelected = selectedDays.length === 5;
  const toggleDay = (d) =>
    setDraft((prev) => {
      const cur = new Set(selectedDays);
      if (cur.has(d)) cur.delete(d);
      else cur.add(d);
      const next = [...cur].sort((a, b) => a - b);
      return { ...prev, days_of_week: next };
    });
  const toggleEveryDay = () =>
    setDraft((prev) => ({
      ...prev,
      days_of_week: allDaysSelected ? [] : [0, 1, 2, 3, 4],
    }));
  // FP3-069 follow-up #11 — One-click weekday presets. Map onto the
  // existing `days_of_week` array so the storage shape (and Wizard /
  // Builder / Health logic) stays untouched. M-F intentionally collapses
  // to the same shape as the "Every day" chip — that's the legacy
  // every-day path and downstream consumers already handle it.
  const PRESETS = {
    mwf: [0, 2, 4],   // Mon / Wed / Fri
    tth: [1, 3],      // Tue / Thu
    mf: [0, 1, 2, 3, 4], // M–F
  };
  const applyPreset = (key) =>
    setDraft((prev) => ({ ...prev, days_of_week: [...(PRESETS[key] || [])] }));
  const presetActive = (key) => {
    const want = PRESETS[key] || [];
    if (selectedDays.length !== want.length) return false;
    return want.every((d) => selectedDays.includes(d));
  };
  const toggleWeek = (w) =>
    setDraft((d) => {
      const cur = Array.isArray(d.weeks_of_month) ? d.weeks_of_month : [];
      const next = cur.includes(w) ? cur.filter((x) => x !== w) : [...cur, w].sort();
      return { ...d, weeks_of_month: next };
    });
  const toggleAllDay = () => {
    setDraft((d) =>
      isAllDay
        ? { ...d, start_time: "09:00", end_time: "10:00" }
        : { ...d, start_time: "00:00", end_time: "23:59" }
    );
  };
  const save = async () => {
    if (!draft.start_time || !draft.end_time || draft.end_time <= draft.start_time) {
      toast.error("End time must be after start time");
      return;
    }
    if (draft.start_date && draft.end_date && draft.end_date < draft.start_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    // FP3-multiday — Require at least one selected weekday so a save
    // can't silently strip an existing day_of_week.
    if (selectedDays.length === 0) {
      toast.error("Select at least one day");
      return;
    }
    const wcN = parseInt(draft.week_cadence, 10);
    const usingCadence = !Number.isNaN(wcN) && wcN > 1;
    if (usingCadence && !(draft.week_cadence_anchor || draft.start_date)) {
      toast.error("Set a start date (or anchor) when using a rolling cadence");
      return;
    }
    setSaving(true);
    try {
      // FP3-multiday — Collapse the selected-days array into the
      // persistence shape:
      //   • 1 day  → day_of_week=<n>,  days_of_week=null
      //   • 5 days → day_of_week=-1,   days_of_week=null   (legacy every-day)
      //   • 2..4   → day_of_week=-1,   days_of_week=[...]  (custom subset;
      //              -1 is a safety fallback for any un-updated consumer.)
      let outDay;
      let outDays;
      if (selectedDays.length === 1) {
        outDay = selectedDays[0];
        outDays = null;
      } else if (selectedDays.length >= 5) {
        outDay = -1;
        outDays = null;
      } else {
        outDay = -1;
        outDays = selectedDays;
      }
      const payload = {
        schedule_period_id: period.id,
        provider_id: providerId,
        student_ids: [],
        block_kind: "personal",
        personal_kind: draft.personal_kind,
        personal_label: draft.personal_label || "",
        day_of_week: outDay,
        days_of_week: outDays,
        start_time: draft.start_time,
        end_time: draft.end_time,
        weeks_of_month: draft.weeks_of_month?.length ? draft.weeks_of_month : null,
        start_date: draft.start_date || null,
        end_date: draft.end_date || null,
        week_cadence: usingCadence ? wcN : null,
        week_cadence_anchor: usingCadence
          ? draft.week_cadence_anchor || draft.start_date || null
          : null,
      };
      if (existing?.id) {
        await api.updateRecurringBlock(existing.id, payload);
      } else {
        await api.createRecurringBlock(payload);
      }
      toast.success(existing ? "Updated" : "Added");
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };
  const activeWeeks = useMemo(
    () => new Set(draft.weeks_of_month || []),
    [draft.weeks_of_month]
  );
  const selectedDaySet = useMemo(() => new Set(selectedDays), [selectedDays]);
  return (
    <div
      data-testid="provider-block-editor"
      className="border border-[#E5E3DB] rounded-lg p-3 bg-[#FAFAF7] space-y-3"
    >
      {/* Row 1 — Kind | Label  (Day chips moved to their own row below) */}
      <div className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-2 items-center">
        <select
          value={draft.personal_kind}
          onChange={(e) => setDraft({ ...draft, personal_kind: e.target.value })}
          data-testid="provider-block-kind"
          className="h-8 text-xs border border-[#d4d2cc] rounded-md px-2 bg-white"
        >
          {nativeOptions(PROVIDER_BLOCK_KINDS, (k) => ({ value: k.value, key: k.value, label: k.label }))}
        </select>
        <Input
          value={draft.personal_label}
          onChange={(e) => setDraft({ ...draft, personal_label: e.target.value })}
          placeholder="Label (e.g. Team eval meeting)"
          data-testid="provider-block-label"
          className="h-8 text-xs"
        />
      </div>

      {/* Row 1b — Weekday chips (FP3-multiday). Lets a single block
          cover any subset of Mon-Fri instead of forcing one row per
          day. Toggling all 5 individually OR clicking "Every day"
          collapses to the legacy day_of_week=-1 shape on save. */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="provider-block-day-chips">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mr-1">
          Days
        </p>
        {DAY_LABELS.map((label, i) => {
          const on = selectedDaySet.has(i);
          return (
            <button
              key={label}
              type="button"
              onClick={() => toggleDay(i)}
              data-testid={`provider-block-day-${i}`}
              aria-pressed={on}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                on
                  ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                  : "bg-white text-[#6B7280] border-[#d4d2cc] hover:bg-[#F3F1EC]"
              }`}
            >
              {label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={toggleEveryDay}
          data-testid="provider-block-day-every"
          aria-pressed={allDaysSelected}
          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
            allDaysSelected
              ? "bg-[#4A6B8C] text-white border-[#4A6B8C]"
              : "bg-white text-[#4A6B8C] border-[#4A6B8C] hover:bg-[#EEF2F6]"
          }`}
        >
          Every day
        </button>
        {/* FP3-069 follow-up #11 — Quick weekday presets so users
            don't have to manually toggle Mon + Wed + Fri (or Tue +
            Thu) every time. M-F collapses to the existing every-day
            shape on save. */}
        <span className="text-[10px] text-[#9B9890] mx-1">·</span>
        {[
          { key: "mwf", label: "M/W/F" },
          { key: "tth", label: "T/Th" },
          { key: "mf", label: "M-F" },
        ].map((p) => {
          const active = presetActive(p.key);
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p.key)}
              data-testid={`provider-block-preset-${p.key}`}
              aria-pressed={active}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                active
                  ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                  : "bg-white text-[#1E3F33] border-[#D9E5DC] hover:bg-[#F2F8F4]"
              }`}
            >
              {p.label}
            </button>
          );
        })}
        {selectedDays.length === 0 && (
          <span className="text-[10px] text-[#B85C5C] italic">Pick at least one day</span>
        )}
      </div>

      {/* Row 2 — Time controls. All-day checkbox sits on the same row,
          inputs slide in beside it when unchecked. */}
      <div className="flex items-center gap-3 flex-wrap">
        <label
          className="flex items-center gap-1.5 text-[11px] font-semibold text-[#4B5563] whitespace-nowrap select-none cursor-pointer"
          data-testid="provider-block-all-day-wrap"
        >
          <input
            type="checkbox"
            checked={isAllDay}
            onChange={toggleAllDay}
            data-testid="provider-block-all-day"
            className="accent-[#1E3F33]"
          />
          All day
        </label>
        {!isAllDay && (
          <div className="flex items-center gap-1">
            <Input
              type="time"
              value={draft.start_time}
              onChange={(e) => setDraft({ ...draft, start_time: e.target.value })}
              data-testid="provider-block-start"
              className="h-8 text-xs w-[110px]"
            />
            <span className="text-[10px] text-[#6B7280]">–</span>
            <Input
              type="time"
              value={draft.end_time}
              onChange={(e) => setDraft({ ...draft, end_time: e.target.value })}
              data-testid="provider-block-end"
              className="h-8 text-xs w-[110px]"
            />
          </div>
        )}
        {isAllDay && (
          <span className="text-[11px] text-[#6B7280] italic">Blocks the entire school day.</span>
        )}
      </div>

      {/* Row 3 — Cadence + dates */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_140px_140px] gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1">
            Weeks of month <span className="font-normal text-[#9B9890]">(none = every week)</span>
          </p>
          <div className="flex items-center gap-1.5">
            {WEEK_OPTIONS.map((w) => {
              const on = activeWeeks.has(w);
              return (
                <button
                  key={w}
                  type="button"
                  onClick={() => toggleWeek(w)}
                  data-testid={`provider-block-week-${w}`}
                  className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
                    on
                      ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                      : "bg-white text-[#6B7280] border-[#d4d2cc] hover:bg-[#F3F1EC]"
                  }`}
                >
                  W{w}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1">
            Rolling cadence
          </p>
          <select
            value={draft.week_cadence || ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                week_cadence: e.target.value ? parseInt(e.target.value, 10) : null,
              })
            }
            data-testid="provider-block-cadence"
            className="h-8 text-xs border border-[#d4d2cc] rounded-md px-2 bg-white w-full"
          >
            <option value="">Every week</option>
            <option value="2">Every 2nd week</option>
            <option value="3">Every 3rd week</option>
            <option value="4">Every 4th week</option>
          </select>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1">
            Start date
          </p>
          <Input
            type="date"
            value={draft.start_date || ""}
            onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
            data-testid="provider-block-start-date"
            className="h-8 text-xs"
          />
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563] mb-1">
            End date
          </p>
          <Input
            type="date"
            value={draft.end_date || ""}
            onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
            data-testid="provider-block-end-date"
            className="h-8 text-xs"
          />
        </div>
      </div>

      {/* Row 4 — explanation + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap pt-1 border-t border-[#ECEAE4]">
        <p className="text-[11px] text-[#6B7280] italic flex-1 min-w-[200px]">
          {draft.week_cadence
            ? `Runs every ${draft.week_cadence} week${draft.week_cadence > 1 ? "s" : ""}${
                draft.start_date ? ` starting the week of ${draft.start_date}` : " — set a start date to anchor the cadence"
              }.`
            : "Leave dates blank for an ongoing block."}
        </p>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            data-testid="provider-block-cancel"
            className="rounded-md h-8 text-[11px] px-3 border-[#d4d2cc]"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            data-testid="provider-block-save"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white rounded-md h-8 text-[11px] px-3 disabled:opacity-40"
          >
            <FloppyDisk size={11} weight="bold" className="mr-1" />
            {saving ? "…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}
